import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useDashboardAuth } from "@/hooks/useDashboardAuth";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useToast } from "@/hooks/use-toast";
import { useActiveMode, useAuthStore } from "@/stores/authStore";
import { useEffect, useRef } from "react";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { logger } from "@/lib/logger";
import { useStudentAuthStore } from "@/stores/studentAuthStore";

/**
 * AdminRoute - Proteção REFORÇADA de rotas administrativas
 * 
 * 🔒 SEGURANÇA CRÍTICA:
 * - Bloqueia navegação do navegador (botão Voltar)
 * - Verifica activeMode ANTES de renderizar
 * - Expulsa INSTANTANEAMENTE usuários não-admin
 */
export const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { profile, loading: authLoading } = useDashboardAuth();
  const { data: userData, isLoading: isLoadingProfile } = useUserProfile(profile?.id ?? null);
  const activeMode = useActiveMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const hasShownToast = useRef(false);
  const studentSession = useStudentAuthStore((state) => state.studentSession);
  const isImpersonating = useStudentAuthStore((state) => state.isImpersonating);

  const isAdmin = userData?.isAdmin ?? false;
  const isLoading = authLoading || isLoadingProfile;
  
  // 🔒 SEGURANÇA P0: Bloquear rotas de configuração para estudantes
  const blockedRoutesForStudents = ['/profile', '/settings', '/central-encontros'];
  const isBlockedStudentRoute = studentSession && blockedRoutesForStudents.some(route => location.pathname.startsWith(route));
  
  // 🔒 Gate de redirecionamento declarativo (sem setTimeout) — ver bloco de early returns abaixo.
  // O efeito antigo com setTimeout(150ms) foi removido para eliminar a janela onde conteúdo admin
  // poderia renderizar momentaneamente para não-admins. A decisão agora é síncrona via <Navigate />.

  // 🔒 CRITICAL FIX: Listener para botão Voltar do navegador
  useEffect(() => {
    const handlePopState = () => {
      // Se o usuário tentou voltar para /admin mas não é admin
      if (location.pathname.startsWith('/admin') && activeMode !== 'admin') {
        logger.warn('🚫 [AdminRoute] Browser back button blocked - not admin');
        
        if (!hasShownToast.current) {
          toast({
            title: "Acesso Negado",
            description: "Você não tem permissão para acessar esta área.",
            variant: "destructive",
          });
          hasShownToast.current = true;
          
          // Reset após 2 segundos
          setTimeout(() => {
            hasShownToast.current = false;
          }, 2000);
        }
        
        // Força redirecionamento
        navigate("/meu-impacto", { replace: true });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [activeMode, location.pathname, navigate, toast]);

  // 🔒 FIX: Sincronizar activeMode para 'admin' APENAS na hidratação inicial (activeMode null)
  // Resolve race condition onde studentAuthStore restaura modo 'student' antes do authStore completar.
  // NÃO deve reagir a trocas manuais de perfil — senão cria loop com useProfileNavigation
  // (usuário escolhe 'teacher' → efeito força volta pra 'admin' → navega pra /admin → repete).
  useEffect(() => {
    if (!isLoading && isAdmin && activeMode === null && !isImpersonating) {
      logger.info('[AdminRoute] Initial sync: activeMode null → admin');
      useAuthStore.getState().setActiveMode('admin');
    }
  }, [isLoading, isAdmin, activeMode, isImpersonating]);

  // Toast para usuários sem role admin
  useEffect(() => {
    if (!isLoading && !isAdmin && profile && !hasShownToast.current) {
      toast({
        title: "Acesso Negado",
        description: "Esta área é restrita para administradores.",
        variant: "destructive",
      });
      hasShownToast.current = true;
    }
  }, [isLoading, isAdmin, profile, toast]);

  // 🔒 Early returns após todos os hooks
  if (isBlockedStudentRoute) {
    logger.warn('[AdminRoute] Student attempted to access blocked route:', location.pathname);
    return <Navigate to="/minhas-experiencias" replace />;
  }

  // 🚀 OPTIMIZATION: Loading Screen dedicado para verificação de permissões
  if (isLoading && !userData) {
    return <LoadingScreen />;
  }

  // 🔒 CRITICAL: Verificação SÍNCRONA antes de renderizar
  // Mas permite renderização durante transição de saída de impersonação
  if (!profile || !isAdmin) {
    return <Navigate to="/meu-impacto" replace />;
  }
  
  // 🔒 Gate declarativo: se um admin autenticado está em modo não-admin (e não impersonando),
  // o useEffect acima vai sincronizar para 'admin' no próximo frame. Mantemos render permissivo
  // aqui porque isAdmin já foi validado acima (linha 105). Isto evita flicker em transições
  // legítimas e remove a necessidade do antigo setTimeout(150ms).
  if (activeMode !== 'admin' && !isImpersonating) {
    logger.info('[AdminRoute] Admin user with non-admin mode, awaiting sync');
  }

  return <>{children}</>;
};
